<template>
  <div class="h-dvh !bg-pitch-black text-total-white">
    <!-- Top Navbar -->
    <TopNavbar />

    <div class="overflow-y-auto max-h-full pb-[100px]">
      <!-- Header -->
      <header class="flex justify-between items-center px-4 py-4">
        <div @click="goBack" class="flex items-center gap-2">
          <button>
            <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back" />
          </button>
          <h1 class="text-prime-golden text-xl">All Activations</h1>
        </div>
        <div class="flex gap-4 items-center">
          <button
            @click="editActivation"
            :disabled="isLoading"
            :class="{ 'opacity-50': isLoading }"
            class="flex items-center gap-1 text-prime-golden text-sm"
          >
            <img src="@/assets/images/edit-pen.svg" alt="Edit" class="w-4 h-4" />
            <span class="underline underline-offset-4">edit</span>
          </button>
        </div>
      </header>

      <!-- Activation Details -->
      <div class="text-center mt-6" :class="{ 'opacity-50': isLoading }">
        <div class="grid grid-cols-[1fr_2fr_1fr] my-2 px-4 items-center">
          <div></div>
          <h2 class="text-2xl flex items-center gap-2 justify-center font-semibold">
            <div
              @click="goToAgent(activationDetails?.activation?.agent?.id)"
              class="flex items-center gap-2 cursor-pointer underline text-prime-golden whitespace-nowrap"
            >
              {{ activationDetails?.activation?.agent?.agent_name || "No Agent" }}
            </div>
          </h2>
          <!--<div class="flex items-center justify-end">
            <button
              @click="handleDeleteActivation"
              class="flex items-center gap-1 text-red-600 text-sm"
            >
              <img src="@/assets/images/trash-icon.svg" alt="Delete" class="w-5 h-5" />
            </button>
          </div>-->
        </div>
        <p class="text-lg font-bold text-gray-400">on</p>
        <div class="grid grid-cols-[1fr_2fr_1fr] mt-1 px-4 items-center">
          <div></div>
          <h2 class="text-2xl flex items-center gap-2 justify-center font-semibold">
            <div
              @click="goToProject(activationDetails?.project?.id)"
              class="flex underline text-prime-golden items-center gap-2 cursor-pointer"
            >
              {{ activationDetails?.project?.address_1 || "N/A" }}
            </div>
          </h2>
          <div class="flex items-center justify-end"></div>
        </div>
        <!--<p class="sm:text-lg font-bold text-gray-400 mb-2">
          {{
            activationDetails?.project?.date_created
              ? `Updated on ${activationDetails?.project?.date_created}`
              : "N/A"
          }}
        </p>-->
      </div>

      <!-- Status Toggle -->
      <div class="text-center my-6" :class="{ 'opacity-50': isLoading }">
        <div
          class="flex items-center gap-2 justify-center"
          :class="{
            'opacity-50': !activationDetails?.activation?.agent?.agent_name, // Less opaque when no agent_name
          }"
        >
          <button
            @click="toggleActivationStatus"
            :disabled="!activationDetails?.activation?.agent?.agent_name"
            class="w-12 h-6 rounded-full flex items-center border-2 border-dark-slate-gray-500 transition duration-300 bg-dark-slate-gray"
          >
            <div
              class="w-5 h-5 bg-prime-golden rounded-full shadow-md transform transition duration-300"
              :class="{ 'translate-x-6': activationDetails?.activation?.is_active }"
            ></div>
          </button>
          <span
            class="text-md font-medium"
            :class="
              activationDetails?.activation?.is_active
                ? 'text-dark-green'
                : 'text-dark-red'
            "
          >
            ● {{ activationDetails?.activation?.is_active ? "activated" : "inactive" }}
          </span>
        </div>
        <p class="text-sm text-gray-400 mt-3">
          {{
            activationDetails?.activation?.is_active
              ? `Activated at ${parseDate(activationDetails?.activation?.date_created)}`
              : ""
          }}
        </p>
      </div>

      <div class="px-4 mt-8" :class="{ 'opacity-50': isLoading }">
        <!-- Activation Instruction -->
        <div class="flex flex-col items-start mb-4">
          <h4 class="text-lg flex gap-2 items-center font-semibold">
            Project Inbox Phone Number
            <img
              src="../../assets/images/copy-icon.svg"
              @click="copyToClipboard(projectManagerTwilioNumber || '')"
            />
          </h4>
          <p class="text-md mt-1 text-total-white-800">
            {{ projectManagerTwilioNumber || "N/A" }}
          </p>
        </div>
        <div class="flex flex-col items-start mt-4">
          <h4 class="text-lg flex gap-2 items-center font-semibold">
            Agent Email Address
            <img
              src="../../assets/images/copy-icon.svg"
              @click="
                copyToClipboard(activationDetails?.activation?.agent_email?.email || '')
              "
            />
          </h4>
          <p class="text-md mt-1 text-total-white-800">
            {{ activationDetails?.activation?.agent_email?.email || "N/A" }}
          </p>
        </div>
        <div class="flex flex-col items-start mt-4">
          <h4 class="text-lg flex gap-2 font-semibold">
            Agent Phone Number
            <img
              src="../../assets/images/copy-icon.svg"
              @click="copyToClipboard(projectAgentTwilioNumber || '')"
            />
          </h4>
          <p class="text-md mt-1 text-total-white-800">
            {{ projectAgentTwilioNumber || "N/A" }}
          </p>
        </div>
        <div class="flex flex-col items-start mt-4">
          <h4 class="text-lg font-semibold">Instructions</h4>
          <p
            class="text-prime-golden underline text-lg"
            @click="goToInstruction(activationDetails?.activation?.instruction?.id)"
            :class="{
              'opacity-50 text-white': !activationDetails?.activation?.instruction?.name,
            }"
          >
            {{ activationDetails?.activation?.instruction?.name || "N/A" }}
          </p>
        </div>
      </div>

      <div class="px-4 mt-8">
        <!-- Provider Agent ID -->
        <div>
          <h3 class="text-lg text-ash-grey flex font-semibold">
            Account:
            <span class="font-bold ml-1"
              >{{ activationDetails?.account?.first_name || "N/A" }}
              {{ activationDetails?.account?.last_name || "" }}</span
            >
          </h3>
          <h3 class="text-lg text-ash-grey flex flex-wrap items-start font-semibold">
            Provider Agent ID:
            <span class="font-bold ml-1">{{
              activationDetails?.activation?.provider_agent_id || "N/A"
            }}</span>
          </h3>
          <h3 class="text-lg text-ash-grey flex flex-wrap font-semibold">
            Provider Thread ID:
            <span class="font-bold ml-1">{{
              activationDetails?.activation?.provider_agent_thread_id || "N/A"
            }}</span>
          </h3>
        </div>
        <!-- Reset Thread -->
        <div
          class="text-center flex justify-start my-6"
          :class="{ 'opacity-50': isLoading }"
        >
          <button
            @click="resetThread"
            class="bg-deep-rust text-white px-3 text-sm py-2 rounded-lg hover:bg-red-700 transition"
          >
            <i class="fa fa-refresh mr-2"></i>
            Reset Thread
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import TopNavbar from "@/components/TopNavbar.vue";
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useActivationStore } from "@/stores/activation";
import { useToast } from "vue-toastification";
import { parseDate } from "@/misc/util";
const router = useRouter();
const route = useRoute();
const activationStore = useActivationStore();
const toast = useToast();

const activationDetails = ref(null);
const projectId = ref(null);
const projectManagerTwilioNumber = ref(null);
const projectAgentTwilioNumber = ref(null);

const isLoading = ref(true);

const fetchActivation = async () => {
  try {
    const projectId = route.params.id;
    activationDetails.value = await activationStore.getActivation(projectId);
  } catch (error) {
    console.error("Error fetching activation:", error);
  }
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

const goToInstruction = (id) => {
  router.push({
    name: "ViewInstructionPage",
    params: { id: id },
    query: {
      from: ["activation", projectId.value],
    },
  });
};

const goToProject = (id) => {
  router.push({
    name: "ProjectPage",
    params: { id },
    query: {
      from: ["activation"],
    },
  });
};

const goToAgent = (id) => {
  router.push({
    name: "AgentViewPage",
    params: { id: id },
    query: {
      from: ["activation", projectId.value],
    },
  });
};

const toggleActivationStatus = async () => {
  try {
    const isActive = activationDetails.value.activation.is_active;
    activationDetails.value.activation.is_active = !activationDetails.value.activation
      .is_active;
    try {
      if (isActive) {
        await activationStore.deactivateProject(activationDetails.value.project.id); // Deactivate the project
      } else {
        await activationStore.deleteProject(activationDetails.value.project.id);

        await activationStore.activateProject({
          project_id: activationDetails.value.project.id,
          agent_id: activationDetails.value.activation.agent.id,
          is_active: activationDetails.value.activation.is_active,
          instruction_id: activationDetails.value.activation.instruction.id,
          agent_email_id: activationDetails.value.activation?.agent_email_id,
          agent_phone: activationDetails.value.activation?.agent_phone,
        }); // Activate the project
      }
    } catch (error) {
      console.error("Error toggling activation status:", error);
    }
  } catch (error) {
    console.error("Error toggling activation status:", error);
  }
};

const resetThread = async () => {
  try {
    await activationStore.resetActivation({
      project_id: activationDetails.value.project.id,
      agent_id: activationDetails.value.activation.agent.id,
      is_active: activationDetails.value.activation.is_active,
      instruction_id: activationDetails.value.activation.instruction.id,
      agent_email_id: activationDetails.value.activation?.agent_email_id,
      agent_phone: activationDetails.value.activation?.agent_phone,
    });
    toast.success("Thread reset successfully.");
  } catch (error) {
    toast.error("Error resetting thread", error);
  }
};

const handleDeleteActivation = async () => {
  try {
    await activationStore.deleteProject(activationDetails.value.project.id);
    router.push({ name: "ActivationsPage" });
  } catch (error) {
    console.error("Error deleting activation:", error);
  }
};

function formatActivationTime(dateString) {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    };

    const time = date.toLocaleTimeString("en-US", options);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    return `activated at ${time} on ${formattedDate}`;
  } catch (error) {
    console.error("Error formatting activation time:", error);
    return null;
  }
}

const editActivation = () => {
  console.log(activationDetails.value.project.id);
  router.push({
    name: "EditActivationPage",
    params: { id: activationDetails.value.project.id },
  });
};

const goBack = () => {
  router.push({ name: "ActivationsPage" });
};

onMounted(async () => {
  await fetchActivation();
  projectManagerTwilioNumber.value =
    activationDetails.value?.activation?.agent_stakeholder?.projects?.[0]?.project_manager_twilio_number?.phone_number;
  projectAgentTwilioNumber.value =
    activationDetails.value?.activation?.agent_stakeholder?.projects?.[0]?.project_agent_twilio_number?.phone_number;
  isLoading.value = false;
  projectId.value = activationDetails.value?.project?.id;
});
</script>
